import {
  every,
  includes,
  isBoolean,
  isEqual,
  isNil,
  isString,
  keys,
  size,
  some,
  values,
} from "lodash";
import { Facet } from "@linkfluence/models/facet";
import { extractCleanedClause } from "./extractor";
import { getFieldFromInnerClause } from "./getter";
import {
  Clause,
  CustomFieldsClause,
  DateRangeClause,
  DateRangeTerm,
  DateRangeTerms,
  DocIdsClause,
  Flag,
  FlagClause,
  Flags,
  InfluencersTagsClause,
  InnerClause,
  NotClause,
  NotTermClause,
  QueryClause,
  QueryTerm,
  QueryTerms,
  RangeClause,
  RangeTerm,
  RangeTerms,
  TermClause,
  WorkspaceClause,
} from "./model";

export function isFlagClause(clause: InnerClause): clause is FlagClause {
  function isFlagField(field: string): field is Flag {
    return includes(Flags, field);
  }
  return isFlagField(keys(clause as FlagClause)[0]); // @TODO
}

export function isCustomFieldsClause(
  clause: InnerClause
): clause is CustomFieldsClause {
  return !!(clause as CustomFieldsClause).customFields;
}

export function isQueryClause(clause: InnerClause): clause is QueryClause {
  function isQueryField(field: string): field is QueryTerm {
    return includes(QueryTerms, field);
  }
  return isQueryField(keys(clause as FlagClause)[0]);
}

export function isNotClause(clause: InnerClause): clause is NotClause {
  return !!(clause as NotClause).not;
}

export function isWorkspaceClause(
  clause: InnerClause
): clause is WorkspaceClause {
  return keys(clause as FlagClause)[0] === "dashboard";
}

export function isRangeClause(clause: InnerClause): clause is RangeClause {
  function isRangeField(field: string): field is RangeTerm {
    return includes(RangeTerms, field);
  }
  return isRangeField(keys(clause as RangeClause)[0]);
}

export function isDateRangeClause(
  clause: InnerClause
): clause is DateRangeClause {
  function isDateRangeField(field: string): field is DateRangeTerm {
    return includes(DateRangeTerms, field);
  }
  return isDateRangeField(keys(clause as DateRangeClause)[0]);
}

export function isInfluencersTagsClause(
  clause: InnerClause
): clause is InfluencersTagsClause {
  const field = getFieldFromInnerClause(clause);
  return field === Facet.InfluencersTags;
}

export function isTermClause(clause: InnerClause): clause is TermClause {
  return (
    !isFlagClause(clause) &&
    !isQueryClause(clause) &&
    !isCustomFieldsClause(clause) &&
    !isNotClause(clause) &&
    !isRangeClause(clause) &&
    !isDateRangeClause(clause) &&
    !isWorkspaceClause(clause) &&
    !isDocIdsClause(clause)
  );
}

export function isInnerClauseEmpty(clause: InnerClause): boolean {
  const field = getFieldFromInnerClause(clause);
  if (isNotClause(clause)) {
    return isInnerClauseEmpty(clause.not);
  } else if (isCustomFieldsClause(clause)) {
    return !size(clause.customFields[field]);
  } else if (isFlagClause(clause)) {
    return !isBoolean(clause[field]);
  } else if (isQueryClause(clause)) {
    return !isString(clause[field]) || clause[field] === "";
  }
  if (isDocIdsClause(clause)) {
    return values(clause.docIds).flat().length === 0;
  } else if (isRangeClause(clause)) {
    const range = clause[field];
    return isNil(range.gt) && isNil(range.lt);
  } else if (isWorkspaceClause(clause)) {
    return !clause.dashboard;
  } else {
    return !size(clause[field]);
  }
}

export function isNotTermClause(clause: InnerClause): clause is NotTermClause {
  return isNotClause(clause) && isTermClause(clause.not);
}

export function isTermClauseOrNotTermClause(
  clause: InnerClause
): clause is NotTermClause | TermClause {
  return isNotTermClause(clause) || isTermClause(clause);
}

export function isDocIdsClause(clause: InnerClause): clause is DocIdsClause {
  const innerClauseKeys = keys(clause);
  return innerClauseKeys.includes(Facet.DocIds);
}

export function isClauseEmpty(clause: Clause) {
  if (clause.and) {
    return every(clause.and, (innerClause) => isInnerClauseEmpty(innerClause));
  } else {
    return true;
  }
}

function arraysHaveSameItems(arr1: any[], arr2: any[]) {
  if (arr1.length !== arr2.length) {
    return false;
  }
  return new Set([...arr1, ...arr2]).size === arr1.length;
}

function areInnerClausesEqual(a: InnerClause, b: InnerClause) {
  if (isNotClause(a) && isNotClause(b)) {
    return areInnerClausesEqual(a.not, b.not);
  } else if (isCustomFieldsClause(a) && isCustomFieldsClause(b)) {
    return areInnerClausesEqual(a.customFields, b.customFields);
  } else if (isTermClause(a) && isTermClause(b)) {
    const aValues = values(a)[0];
    const bValues = values(b)[0];
    return arraysHaveSameItems(aValues, bValues);
  } else {
    return isEqual(a, b);
  }
}

export function areClausesEqual(a: Clause, b: Clause) {
  const clausesA = extractCleanedClause(a).and;
  const clausesB = extractCleanedClause(b).and;

  if (size(clausesA) !== size(clausesB)) {
    return false;
  }
  if (size(clausesA) === 0) {
    return true;
  }

  return every(clausesA, (clauseA) =>
    some(clausesB, (clauseB) => areInnerClausesEqual(clauseA, clauseB))
  );
}
export function getNotClause(clause) {
  return clause.and.find((value) => Object.keys(value) + "" === "not");
}

export function isClauseFieldFilled(clause: Clause, field: string) {
  const getAndClause = (clause: Clause, field: string) =>
    clause.and.find((value) => Object.keys(value) + "" === field);

  const getCustomClause = (clause) =>
    clause.and.find((value) => Object.keys(value) + "" === "customFields");
  const booleanFieldValue = (value) => {
    return isNil(value)
      ? false
      : field === Facet.DocIds
      ? values(value).flat().length > 0
      : Array.isArray(value)
      ? value.length > 0
      : typeof value === "boolean"
      ? true
      : JSON.stringify(value) ===
        JSON.stringify({ gt: "now-110y", lt: "now-0y" })
      ? false
      : Object.values(value).filter((v) => v).length === 0
      ? false
      : !!value;
  };

  const and = getAndClause(clause, field)
    ? getAndClause(clause, field)[field]
    : undefined;
  const not = getNotClause(clause)
    ? getNotClause(clause).not[field]
    : undefined;
  const customField = getCustomClause(clause)
    ? getCustomClause(clause).customFields[field]
    : undefined;
  return (
    booleanFieldValue(and) ||
    booleanFieldValue(not) ||
    booleanFieldValue(customField)
  );
}

export function isSandboxedClause(innerClause: InnerClause): boolean {
  const field = getFieldFromInnerClause(innerClause);
  return field === "inRelationship" || field === "hasChildren";
}
